import { z } from "zod";
import { EXPERT_CATEGORIES, type ExpertCategory } from "../enums";
import { LIMITS } from "../limits";
import { cents, email, halfStep, optionalText, requiredText } from "./common";
import type { ExpertV2Fields } from "./v1-additions";

// ---------- Requests ----------

const expertFields = {
  name: requiredText(LIMITS.expertName, "Name"),
  email: email.nullable(),
  category: z.enum(EXPERT_CATEGORIES),
  positionTitle: optionalText(LIMITS.positionTitle, "Position"),
  dayBudget: halfStep(LIMITS.dayBudgetMax).nullable(),
  feeRateCents: cents(LIMITS.feeRateCentsMax).nullable(),
};

export const createExpertBody = z
  .object({
    name: expertFields.name,
    email: expertFields.email.default(null),
    category: expertFields.category,
    positionTitle: expertFields.positionTitle.default(""),
    dayBudget: expertFields.dayBudget.default(null),
    feeRateCents: expertFields.feeRateCents.default(null),
  })
  .strict();
export type CreateExpertBody = z.input<typeof createExpertBody>;
export type CreateExpertInput = z.output<typeof createExpertBody>;

export const updateExpertBody = z
  .object({
    name: expertFields.name.optional(),
    email: expertFields.email.optional(),
    category: expertFields.category.optional(),
    positionTitle: expertFields.positionTitle.optional(),
    dayBudget: expertFields.dayBudget.optional(),
    feeRateCents: expertFields.feeRateCents.optional(),
  })
  .strict();
export type UpdateExpertBody = z.infer<typeof updateExpertBody>;

// ---------- Responses ----------

export type Expert = {
  id: string;
  contractId: string;
  name: string;
  email: string | null;
  category: ExpertCategory;
  positionTitle: string;
  /** half-days allowed (a number in steps of 0.5) */
  dayBudget: number | null;
  /** per day */
  feeRateCents: number | null;
  /** Σ quantity of expert_days lines linked to this expert on non-withdrawn ToRs */
  plannedDays: number;
  createdAt: string;
  updatedAt: string;
} & ExpertV2Fields;
